import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Stack, Alert } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useOverlay from '@/hooks/useOverlay'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import NotFound from '@/pages/NotFound'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'
import { useGetPubModels } from '@/service-library/hooks/pub-models'
import useIsSuperUserVisible from '@/services/hooks/useIsSuperUserVisible'
import { notNull } from '@/utils/typescript'
import Drawer from '@/components/drawer/Drawer'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsNavigation from '@/components/settings-navigation/SettingsNavigation'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

const qualityRoutes: RouteDefinition[] = [
  {
    to: 'document-rules',
    label: 'Document Rules',
    keepQueryParams: true,
  },
  {
    to: 'field-rules',
    label: 'Field Rules',
    keepQueryParams: true,
  },
]

type RouteDefinition = {
  to: string
  label: string
  isSuperUserOnly?: boolean
  keepQueryParams?: boolean
}

export default function ProjectSettingsLayout() {
  const { project } = useProjectContext()
  const { isAuthorized, isInitialized } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const isSuperUserVisible = useIsSuperUserVisible()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const [errors, setErrors] = useState<string[] | null>(null)
  const drawerOverlay = useOverlay({ initiallyOpen: true })

  const { isEnabled: isPurchaseOrdersEnabled } =
    useProjectFeatureEnabled('purchase-orders')

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1',
      project_id: project.id,
    },
  })

  const { pubModels } = useGetPubModels({
    filters: {
      project_model_id: selectedWorkflow?.project_model_id || undefined,
      fields__only: 'id',
      limit: '1',
    },
    enabled: !!selectedWorkflow?.project_model_id,
  })

  const routes: RouteDefinition[] = [
    {
      to: 'general',
      label: 'General',
      keepQueryParams: true,
    },
    isSuperUserVisible
      ? {
          to: 'features',
          label: 'Features',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
    {
      to: 'fields',
      label: 'Fields',
      keepQueryParams: true,
    },
    isPurchaseOrdersEnabled
      ? {
          to: 'purchase-orders',
          label: 'Purchase Order Matching',
          keepQueryParams: true,
        }
      : null,
    {
      to: 'model-library',
      label: 'Model Library',
      keepQueryParams: true,
    },
    {
      to: 'tags',
      label: 'Document Tags',
      keepQueryParams: true,
    },
    isSuperUserVisible && projectTemplates.length > 0
      ? {
          to: 'template',
          label: 'Template',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
    {
      to: 'model-tables',
      label: 'Model Tables',
      keepQueryParams: true,
    },
    isSuperUserVisible
      ? {
          to: 'advanced-settings',
          label: 'Advanced Settings',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
  ].filter(Boolean) as RouteDefinition[]

  const workflowRoutes: RouteDefinition[] = [
    {
      to: 'workflow-states',
      label: 'Workflow States',
      keepQueryParams: true,
    },
    selectedWorkflow.project_model_id
      ? {
          to: 'model-configuration',
          label: 'Model Configuration',
          keepQueryParams: true,
        }
      : null,
    isSuperUserVisible && pubModels.length > 0
      ? {
          to: 'published-model-management',
          label: 'Published Model Management',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
  ].filter(notNull)

  if (!isInitialized) return null
  if (!isAuthorized) return <NotFound />

  return (
    <>
      <ProjectBreadcrumb
        label="Settings"
        url={`/projects/${project.id}/settings/general`}
      />
      <Drawer
        overlay={drawerOverlay}
        size={isPurchaseOrdersEnabled ? 'md' : 'sm'}
        transparent
      >
        <SettingsNavigation label="Project Settings" routes={routes} />
        <SettingsNavigation label="Quality Control" routes={qualityRoutes} />
        <SettingsNavigation
          label="Workflow Settings"
          routes={workflowRoutes}
          subHeader={selectedWorkflow.name}
        />
      </Drawer>

      <Stack
        direction="column"
        p={2}
        pt={3}
        width="100%"
        flexGrow={1}
        sx={{ minHeight: 0 }}
      >
        {errors && <Alert severity="error">{errors}</Alert>}
        <Outlet
          context={{
            setWizardErrors: setErrors,
            isSettings: true,
          }}
        />
      </Stack>
    </>
  )
}
